import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Smartphone, Tablet, Code, FolderOpen, Copy, Check } from 'lucide-react'

const Instructions = () => {
  const [activeTab, setActiveTab] = useState('ios')
  const [copiedCode, setCopiedCode] = useState(null)

  // Syntax highlighting function
  const formatCodeLine = (line, platform) => {
    if (!line) return <span className="text-gray-300">{line}</span>

    // XML/Android Manifest highlighting - Enhanced for incomplete tags
    if (line.includes('<')) {
      return (
        <span>
          {line.split(/(<[^>]*>|<[^>\s]*)/g).map((part, i) => {
            // Handle complete tags like <application>, </application>
            if (part.startsWith('<') && part.endsWith('>')) {
              if (part.startsWith('</')) {
                // Closing tag like </application>
                const tagName = part.slice(2, -1)
                return (
                  <span key={i}>
                    <span className="text-blue-400">&lt;/</span>
                    <span className="text-red-400">{tagName}</span>
                    <span className="text-blue-400">&gt;</span>
                  </span>
                )
              } else if (part.includes('=')) {
                // Opening tag with attributes
                const tagContent = part.slice(1, -1)
                const spaceIndex = tagContent.indexOf(' ')
                const tagName = spaceIndex > -1 ? tagContent.substring(0, spaceIndex) : tagContent
                const attributes = spaceIndex > -1 ? tagContent.substring(spaceIndex + 1) : ''
                
                return (
                  <span key={i}>
                    <span className="text-blue-400">&lt;</span>
                    <span className="text-red-400">{tagName}</span>
                    {attributes && (
                      <>
                        <span className="text-gray-300"> </span>
                        {attributes.split(/(\w+="[^"]*")/g).map((attr, j) => {
                          if (attr.includes('=')) {
                            const [key, value] = attr.split('=')
                            return (
                              <span key={j}>
                                <span className="text-yellow-400">{key}</span>
                                <span className="text-gray-300">=</span>
                                <span className="text-green-400">{value}</span>
                              </span>
                            )
                          }
                          return <span key={j} className="text-gray-300">{attr}</span>
                        })}
                      </>
                    )}
                    <span className="text-blue-400">&gt;</span>
                  </span>
                )
              } else {
                // Simple opening tag without attributes
                const tagName = part.slice(1, -1)
                return (
                  <span key={i}>
                    <span className="text-blue-400">&lt;</span>
                    <span className="text-red-400">{tagName}</span>
                    <span className="text-blue-400">&gt;</span>
                  </span>
                )
              }
            }
            // Handle incomplete opening tags (like "<application" without closing >)
            else if (part.startsWith('<') && !part.endsWith('>')) {
              const tagName = part.slice(1).trim()
              return (
                <span key={i}>
                  <span className="text-blue-400">&lt;</span>
                  <span className="text-red-400">{tagName}</span>
                </span>
              )
            }
            // Handle attributes on separate lines
            else if (part.includes('=') && part.includes('"')) {
              return (
                <span key={i}>
                  {part.split(/(\s*\w+="[^"]*")/g).map((attr, j) => {
                    if (attr.includes('=')) {
                      const trimmed = attr.trim()
                      const [key, value] = trimmed.split('=')
                      return (
                        <span key={j}>
                          <span className="text-gray-300">    </span>
                          <span className="text-yellow-400">{key}</span>
                          <span className="text-gray-300">=</span>
                          <span className="text-green-400">{value}</span>
                        </span>
                      )
                    }
                    return <span key={j} className="text-gray-300">{attr}</span>
                  })}
                </span>
              )
            }
            // Handle closing bracket and tag end
            else if (part.includes('>')) {
              return (
                <span key={i}>
                  {part.split(/(>)/).map((subpart, k) => {
                    if (subpart === '>') {
                      return <span key={k} className="text-blue-400">&gt;</span>
                    }
                    return <span key={k} className="text-gray-200">{subpart}</span>
                  })}
                </span>
              )
            }
            return <span key={i} className="text-gray-200">{part}</span>
          })}
        </span>
      )
    }

    // File paths highlighting
    if (line.includes('/') || line.includes('.')) {
      return (
        <span>
          {line.split(/(\.[a-zA-Z]+|\/)/).map((part, i) => {
            if (part === '/') {
              return <span key={i} className="text-blue-400">/</span>
            }
            if (part.startsWith('.')) {
              return <span key={i} className="text-green-400">{part}</span>
            }
            return <span key={i} className="text-purple-300">{part}</span>
          })}
        </span>
      )
    }

    // iOS specific highlighting
    if (platform === 'ios' && line.includes('→')) {
      return (
        <span>
          {line.split('→').map((part, i) => (
            <span key={i}>
              <span className="text-cyan-400">{part.trim()}</span>
              {i < line.split('→').length - 1 && (
                <span className="text-gray-400"> → </span>
              )}
            </span>
          ))}
        </span>
      )
    }

    // Default colorful text
    return <span className="text-gray-200">{line}</span>
  }

  // Copy to clipboard function
  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  const iosSteps = [
    {
      step: 1,
      title: 'Download & Extract',
      description: 'Download the generated ZIP file and extract the iOS folder',
      code: 'iOS/AppIcon.appiconset/'
    },
    {
      step: 2,
      title: 'Open Xcode Project',
      description: 'Open your iOS project in Xcode and navigate to Assets.xcassets',
      code: null
    },
    {
      step: 3,
      title: 'Replace AppIcon Set',
      description: 'Drag the AppIcon.appiconset folder into Assets.xcassets to replace the existing one',
      code: null
    },
    {
      step: 4,
      title: 'Verify in Project Settings',
      description: 'Go to your target settings and ensure the App Icon Source is set correctly',
      code: 'Target → General → App Icons and Launch Images'
    }
  ]

  const androidSteps = [
    {
      step: 1,
      title: 'Download & Extract',
      description: 'Download the generated ZIP file and extract the Android folder',
      code: 'Android/res/mipmap-*/'
    },
    {
      step: 2,
      title: 'Navigate to Project',
      description: 'Open your Android project and go to app/src/main/res/',
      code: 'app/src/main/res/'
    },
    {
      step: 3,
      title: 'Replace Icon Files',
      description: 'Replace the contents of each mipmap folder with the corresponding icons',
      code: 'mipmap-hdpi/ic_launcher.png\nmipmap-mdpi/ic_launcher.png\nmipmap-xhdpi/ic_launcher.png\nmipmap-xxhdpi/ic_launcher.png\nmipmap-xxxhdpi/ic_launcher.png'
    },
    {
      step: 4,
      title: 'Update Manifest',
      description: 'Ensure your AndroidManifest.xml references the correct icon',
      code: '<application\n    android:icon="@mipmap/ic_launcher"\n    android:roundIcon="@mipmap/ic_launcher_round"\n    android:theme="@style/AppTheme">\n</application>'
    }
  ]

  const iconSizes = {
    ios: [
      { name: 'App Store', size: '1024×1024', usage: 'App Store submission' },
      { name: 'iPhone @3x', size: '180×180', usage: 'iPhone home screen (3x)' },
      { name: 'iPhone @2x', size: '120×120', usage: 'iPhone home screen (2x)' },
      { name: 'iPad Pro', size: '167×167', usage: 'iPad Pro home screen' },
      { name: 'iPad @2x', size: '152×152', usage: 'iPad home screen (2x)' },
      { name: 'Spotlight @3x', size: '120×120', usage: 'Spotlight search (3x)' },
      { name: 'Settings @3x', size: '87×87', usage: 'Settings app (3x)' },
    ],
    android: [
      { name: 'xxxhdpi', size: '192×192', usage: '4x density (480dpi)' },
      { name: 'xxhdpi', size: '144×144', usage: '3x density (320dpi)' },
      { name: 'xhdpi', size: '96×96', usage: '2x density (240dpi)' },
      { name: 'hdpi', size: '72×72', usage: '1.5x density (160dpi)' },
      { name: 'mdpi', size: '48×48', usage: '1x density (120dpi)' },
      { name: 'ldpi', size: '36×36', usage: '0.75x density (120dpi)' },
    ]
  }

  return (
    <section id="instructions" className="py-16 px-4 sm:px-6 lg:px-0 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How to Use Your Icons
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Step-by-step guides for integrating your generated icons into iOS and Android projects
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-white p-1 rounded-xl shadow-soft">
            <div className="flex space-x-1">
              {[
                { id: 'ios', name: 'iOS Setup', icon: Smartphone },
                { id: 'android', name: 'Android Setup', icon: Tablet }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Setup Instructions */}
          <motion.div
            key={activeTab}
            className="bg-white rounded-xl p-6 shadow-soft"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FolderOpen className="w-5 h-5 text-primary-600" />
              <span>{activeTab === 'ios' ? 'iOS' : 'Android'} Integration Steps</span>
            </h3>
            
            <div className="space-y-6">
              {(activeTab === 'ios' ? iosSteps : androidSteps).map((step, index) => (
                <motion.div
                  key={index}
                  className="flex space-x-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-semibold text-sm">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-gray-600 text-sm mb-2">{step.description}</p>
                    {step.code && (
                      <div className="relative group">
                        <pre className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-gray-100 p-4 rounded-lg text-sm border border-slate-700 shadow-inner overflow-hidden">
                          <code className="block font-mono leading-relaxed">
                            {step.code.split('\n').map((line, lineIndex) => (
                              <div key={lineIndex} className="flex items-start">
                                <span className="text-slate-500 text-xs mr-3 select-none w-6 text-right leading-relaxed flex-shrink-0 mt-0.5">
                                  {lineIndex + 1}
                                </span>
                                <span className="flex-1 leading-relaxed break-all whitespace-pre-wrap word-break">
                                  {formatCodeLine(line, activeTab)}
                                </span>
                              </div>
                            ))}
                          </code>
                        </pre>
                        <button
                          onClick={() => copyToClipboard(step.code)}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-slate-400 hover:text-white p-1 rounded bg-slate-700/50 hover:bg-slate-600/50"
                          title="Copy code"
                        >
                          {copiedCode === step.code ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Icon Sizes Reference */}
          <motion.div
            className="bg-white rounded-xl p-6 shadow-soft"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <Code className="w-5 h-5 text-primary-600" />
              <span>{activeTab === 'ios' ? 'iOS' : 'Android'} Icon Sizes</span>
            </h3>
            
            <div className="space-y-3 overflow-y-auto">
              {iconSizes[activeTab].map((size, index) => (
                <motion.div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border border-gray-100"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{size.name}</div>
                    <div className="text-xs text-gray-500">{size.usage}</div>
                  </div>
                  <div className="text-sm font-mono text-white bg-gradient-to-r from-primary-500 to-primary-600 px-3 py-1 rounded-full shadow-sm">
                    {size.size}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Additional Tips */}
        <motion.div
          className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center space-x-2">
            <span className="text-2xl">💡</span>
            <span>Pro Tips</span>
          </h4>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>Always test your icons on real devices to ensure they look good at different sizes</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>Consider how your icon will look on both light and dark backgrounds</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>Keep your design simple and avoid small details that may not be visible at smaller sizes</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>Use the App Store and Google Play guidelines for best practices</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </section>
  )
}

export default Instructions 